import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ChangePassword from "@/components/ChangePassword";

const gbp = (n) => `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 0 })}`;

export default function Account() {
    const { user, loading, logout } = useAuth();
    const [tab, setTab] = useState("orders");
    const [orders, setOrders] = useState([]);
    const [subs, setSubs] = useState([]);

    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const [o, s] = await Promise.all([
                    api.get("/orders/mine"),
                    api.get("/consignments/mine"),
                ]);
                setOrders(o.data.items || []);
                setSubs(s.data.items || []);
            } catch (e) {
                console.error(e);
            }
        })();
    }, [user]);

    if (loading) return <div className="loading">Loading…</div>;
    if (!user) return <Navigate to="/login?next=/account" replace />;

    return (
        <div className="account" data-testid="account-page">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
                <div>
                    <div className="eyebrow" style={{ marginBottom: 8 }}>Account</div>
                    <h1 className="h-section" style={{ fontStyle: "italic" }}>Welcome, {user.name?.split(" ")[0] || "you"}.</h1>
                </div>
                <button className="btn btn-outline" onClick={logout} data-testid="account-logout">
                    Sign Out
                </button>
            </div>

            <div className="account-tabs">
                <button className={`account-tab ${tab === "orders" ? "active" : ""}`} onClick={() => setTab("orders")} data-testid="account-tab-orders">
                    Orders
                </button>
                <button className={`account-tab ${tab === "consignments" ? "active" : ""}`} onClick={() => setTab("consignments")} data-testid="account-tab-consignments">
                    My Consignments
                </button>
                <button className={`account-tab ${tab === "password" ? "active" : ""}`} onClick={() => setTab("password")} data-testid="account-tab-password">
                    Password
                </button>
            </div>

            {tab === "orders" && (
                <div data-testid="account-orders">
                    {orders.length === 0 ? (
                        <div className="empty-state">
                            No orders yet.
                            <div style={{ marginTop: 16 }}>
                                <Link to="/shop" className="link-underline">Browse the Edit</Link>
                            </div>
                        </div>
                    ) : (
                        orders.map((o) => (
                            <div key={o.id} className="account-order">
                                <div>
                                    <div className="meta">Order · {o.id.slice(-8)}</div>
                                    <div style={{ fontFamily: "var(--font-serif)", marginTop: 4 }}>
                                        {o.line_items?.map((li) => li.title).join(", ")}
                                    </div>
                                    <div style={{ marginTop: 6 }}>
                                        <span className={`status-pill ${o.status}`}>{o.status}</span>
                                    </div>
                                </div>
                                <div style={{ textAlign: "right" }}>
                                    <div style={{ fontFamily: "var(--font-serif)", fontSize: "1.2rem" }}>{gbp(o.total)}</div>
                                    <Link to={`/order/${o.id}`} className="link-underline" style={{ fontSize: 12 }}>View</Link>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {tab === "consignments" && (
                <div data-testid="account-consignments">
                    {subs.length === 0 ? (
                        <div className="empty-state">
                            You haven&rsquo;t submitted any pieces yet.
                            <div style={{ marginTop: 16 }}>
                                <Link to="/sell" className="link-underline">Start a submission</Link>
                            </div>
                        </div>
                    ) : (
                        subs.map((s) => (
                            <div key={s.id} className="account-order">
                                <div>
                                    <div className="meta">Submission · {new Date(s.created_at).toLocaleDateString("en-GB")}</div>
                                    <div style={{ fontFamily: "var(--font-serif)", marginTop: 4 }}>
                                        {s.items?.length} piece{s.items?.length === 1 ? "" : "s"} —{" "}
                                        {s.items?.slice(0, 3).map((i) => i.designer).join(", ")}
                                    </div>
                                    <div style={{ marginTop: 6 }}>
                                        <span className={`status-pill ${s.status}`}>{s.status.replace(/_/g, " ")}</span>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {tab === "password" && (
                <div data-testid="account-password">
                    <ChangePassword />
                </div>
            )}
        </div>
    );
}
